import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { guardarDestino } from '@/lib/destino';
import Dica from '@/components/feature/Dica';
import CartaoPorta from '@/pages/criar/components/CartaoPorta';
import { PORTAS, iconeDoEvento } from '@/pages/criar/portas';

interface Rascunho {
  id: number;
  nome: string;
  categoria: string | null;
  parouEm: string;
}

export default function CriarFormularioPage() {
  const navigate = useNavigate();
  const { user, pronto } = useAuth();
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [rascunhos, setRascunhos] = useState<Rascunho[]>([]);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    if (!pronto) return;
    if (!user) {
      guardarDestino('/criar/formulario');
      navigate('/entrar?voltar=/criar/formulario', { replace: true });
      return;
    }
    let ativo = true;

    async function carregar() {
      setCarregando(true);
      setErro(null);
      try {
        const { data: idsData, error: errIds } = await supabase.rpc('meus_eventos');
        if (errIds) throw errIds;
        const ids = ((idsData as unknown[]) || [])
          .map((n) => Number(n))
          .filter((n) => Number.isFinite(n) && n > 0);

        let lista: Rascunho[] = [];
        if (ids.length > 0) {
          const [resEv, resMod] = await Promise.all([
            supabase
              .from('eventos')
              .select('id, nome, categoria')
              .in('id', ids)
              .eq('status', 'rascunho')
              .order('atualizado_em', { ascending: false }),
            supabase.from('modalidades').select('evento_id').in('evento_id', ids),
          ]);
          if (resEv.error) throw resEv.error;
          if (resMod.error) throw resMod.error;

          const comModalidade = new Set<number>(
            ((resMod.data as { evento_id: number }[]) || []).map((m) => m.evento_id),
          );

          lista = ((resEv.data as { id: number; nome: string; categoria: string | null }[]) || []).map(
            (e) => ({
              id: e.id,
              nome: e.nome,
              categoria: e.categoria,
              parouEm: comModalidade.has(e.id) ? 'Datas do evento' : 'Inscrições',
            }),
          );
        }

        if (ativo) setRascunhos(lista);
      } catch (e) {
        if (ativo) {
          setErro(
            mensagemAmigavel(e, 'Não foi possível carregar. Tente novamente.'),
          );
        }
      } finally {
        if (ativo) setCarregando(false);
      }
    }

    carregar();
    return () => {
      ativo = false;
    };
  }, [navigate, reload, pronto, user]);

  return (
    <div className="min-h-screen bg-background-100">
      <header className="sticky top-0 z-20 border-b border-secondary-200 bg-white">
        <div className="mx-auto flex h-16 w-full max-w-[720px] items-center px-4 md:px-6">
          <Link
            to="/criar"
            className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap text-sm font-medium text-foreground-700 transition-colors hover:text-foreground-950"
          >
            <span className="flex h-5 w-5 items-center justify-center">
              <i className="ri-arrow-left-line" />
            </span>
            Voltar
          </Link>
        </div>
      </header>

      <main className="mx-auto w-full max-w-[720px] px-4 py-8 md:px-6">
        <div className="flex items-center gap-2">
          <h1 className="text-[28px] font-normal text-foreground-950">O que você quer criar?</h1>
          <Dica id="criar.portas" />
        </div>
        <p className="mt-1 text-sm text-secondary-500">
          Escolha o que você quer fazer. A gente guia o caminho.
        </p>
        <Link
          to="/conheca"
          className="mt-2 inline-flex cursor-pointer items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-question-line" />
          </span>
          Primeira vez? Veja o passo a passo
        </Link>

        {carregando ? (
          <div className="mt-8 space-y-4">
            <div className="h-24 w-full animate-pulse rounded-2xl bg-secondary-100" />
            <div className="h-24 w-full animate-pulse rounded-2xl bg-secondary-100" />
            <div className="h-24 w-full animate-pulse rounded-2xl bg-secondary-100" />
            <div className="h-24 w-full animate-pulse rounded-2xl bg-secondary-100" />
          </div>
        ) : erro ? (
          <div className="mt-8 rounded-lg border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700">
            {erro}
            <button
              type="button"
              onClick={() => setReload((r) => r + 1)}
              className="ml-2 cursor-pointer font-medium underline"
            >
              Tentar de novo
            </button>
          </div>
        ) : (
          <>
            {rascunhos.length > 0 && (
              <section className="mt-8">
                <h2 className="text-sm font-medium text-foreground-700">Continuar criando</h2>
                <div className="mt-2 overflow-hidden rounded-2xl border border-[#E7E3EF] bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
                  {rascunhos.map((r, i) => (
                    <div
                      key={r.id}
                      className={`flex items-center gap-4 p-5 ${
                        i > 0 ? 'border-t border-[#E7E3EF]' : ''
                      }`}
                    >
                      <span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center text-primary-600">
                        <i className={`${iconeDoEvento(r.categoria)} text-[26px]`} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[15px] font-medium text-foreground-950">
                          {r.nome}
                        </p>
                        <p className="mt-0.5 text-sm text-secondary-500">Parou em &quot;{r.parouEm}&quot;</p>
                      </div>
                      <Link
                        to={`/evento/${r.id}/editar`}
                        className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-600"
                      >
                        Continuar
                      </Link>
                    </div>
                  ))}
                </div>
              </section>
            )}

            <div className="mt-8 space-y-3">
              {PORTAS.map((porta) => (
                <CartaoPorta key={porta.chave} porta={porta} />
              ))}
            </div>

            <div className="mt-8 text-center">
              <Link
                to="/criar/outro"
                className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap text-sm text-secondary-500 transition-colors hover:text-primary-600"
              >
                Outro tipo de evento
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-arrow-right-line" />
                </span>
              </Link>
            </div>
          </>
        )}
      </main>
    </div>
  );
}